/**
 * Quote-side API client — labor estimate + drawing-to-paneliq import.
 */
import { apiBaseUrl, getToken, clearToken } from './auth.js';

async function _postJson(path, body) {
  const token = getToken();
  if (!token) throw new Error('Not signed in. Please sign in again.');
  const response = await fetch(apiBaseUrl() + path, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (response.status === 401) {
    clearToken();
    throw new Error('Session expired. Please sign in again.');
  }
  let data = null;
  try { data = await response.json(); } catch { /* leave null */ }
  if (!response.ok) {
    const detail = (data && data.detail) || `API error ${response.status}`;
    throw new Error(detail);
  }
  return data;
}

/**
 * @param {Array<{tag:string,category:string,description?:string,qty:number}>} bomLines
 * @param {Array<{gauge:string,count:number}>=} wireRuns
 * @returns {Promise<object>}
 */
export async function fetchLaborEstimate(bomLines, wireRuns) {
  if (!bomLines || bomLines.length === 0) {
    throw new Error('Cannot estimate labor: BOM is empty.');
  }
  const body = { bom_lines: bomLines };
  if (wireRuns && wireRuns.length > 0) body.wire_runs = wireRuns;
  return _postJson('/quote/labor_estimate', body);
}

/**
 * Materialize the current quick-quote drawing extraction into a paneliq
 * Project + Design + Revision + panel_bom_line rows. After this returns,
 * the operator can open Streamlit at /pages/01_Panel_Builder to see the
 * new revision flow through gates / compliance / pricing.
 *
 * @returns {Promise<{
 *   project_id:string, project_name:string, project_created:boolean,
 *   design_id:string, design_name:string, design_created:boolean,
 *   revision_id:string, rev:number, bom_lines_inserted:number,
 * }>}
 */
export async function importDrawingToPaneliq({ projectName, designName, starterType, loads, meta }) {
  if (!projectName || !projectName.trim()) {
    throw new Error('Project name is required.');
  }
  if (!loads || loads.length === 0) {
    throw new Error('No loads to import. Upload and analyze a drawing first.');
  }
  return _postJson('/quote/import_drawing', {
    project_name: projectName.trim(),
    design_name: designName ? designName.trim() : undefined,
    starter_type: starterType === 'vfd' ? 'vfd' : 'soft',
    loads,
    meta: meta || null,
  });
}

/**
 * Translate the React app's in-memory quote state into a flat BOM that the
 * backend labor service can score. Each line carries a `tag` so the React
 * caller can group results back to its loads / infra rows.
 *
 *   loads: array of { tag, hp, nemaSize, description }
 *   starterType: 'soft' | 'vfd'
 *   infraRows: array of { l, mat, hrs, note }  (from calcInfraCost)
 */
export function buildPanelBom(loads, starterType, infraRows) {
  const lines = [];

  loads.forEach((ld) => {
    const tag = ld.tag || 'LOAD';
    const hp = ld.hp || 0;
    const desc = `${hp}HP ${ld.description || ''}`.trim();

    if (hp === 0) {
      lines.push({ tag, category: 'circuit_breaker', qty: 1, description: desc || 'Feeder breaker' });
    } else if (starterType === 'soft') {
      lines.push({ tag, category: 'soft_starter',    qty: 1, description: desc });
      lines.push({ tag, category: 'circuit_breaker', qty: 1, description: 'Branch CB' });
      lines.push({ tag, category: 'overload_relay',  qty: 1, description: 'OL relay' });
    } else {
      lines.push({ tag, category: 'vfd',             qty: 1, description: desc });
      lines.push({ tag, category: 'circuit_breaker', qty: 1, description: 'Branch CB' });
    }
  });

  // Infra rows: assign predictable tags so the frontend can map results back
  // by index. Category is inferred from the human-readable label.
  (infraRows || []).forEach((row, i) => {
    const tag = `infra:${i}`;
    const label = (row.l || row.label || `infra-${i}`);
    const lower = label.toLowerCase();

    let category = 'enclosure';
    let qty = 1;
    if (lower.includes('structure')) {
      category = 'enclosure';
      const m = label.match(/(\d+)\s*sec/i);
      qty = m ? parseInt(m[1], 10) : 1;
    } else if (lower.includes('main') && lower.includes('breaker')) {
      category = 'main_disconnect';
    } else if (lower.includes('bus')) {
      category = 'breaker';
    } else if (lower.includes('surge')) {
      category = 'surge_protector';
    } else if (lower.includes('meter')) {
      category = 'meter';
    } else if (lower.includes('cpt')) {
      category = 'control_transformer';
      const m = label.match(/x(\d+)/i);
      qty = m ? parseInt(m[1], 10) : 1;
    } else if (lower.includes('transformer') || lower.includes('xfmr')) {
      category = 'transformer';
    } else if (lower.includes('ethernet') || lower.includes('switch')) {
      category = 'plc_module';
      const m = label.match(/x(\d+)/i);
      qty = m ? parseInt(m[1], 10) : 1;
    } else if (lower.includes('psu') || lower.includes('24v')) {
      category = 'power_supply';
      const m = label.match(/x(\d+)/i);
      qty = m ? parseInt(m[1], 10) : 1;
    } else if (lower.includes('pdp')) {
      category = 'enclosure';
    }

    lines.push({ tag, category, qty, description: label });
  });

  return lines;
}
